import { ENTITY_ICONS } from "@/constants/entity-icons"
import { SOFIA_PRODUCT_NAME } from "@/constants/sofia"
import { CREATE_TEMPLATE_INTENT_PATH } from "@/templates/navigation"
import {
  Activity,
  Bell,
  BotMessageSquare,
  ClipboardCheck,
  FileText,
  Gauge,
  Home,
  LineChart,
  Mic,
  Puzzle,
  RefreshCw,
  Search,
  Volume2,
} from "lucide-react"

import { CovalMark } from "@/components/icons/CovalLogo"
import { MessageSquareArrowUp } from "@/components/icons/MessageSquareArrowUp"

import type { NavigationConfigItem } from "../types"

export const HOME_BASE_HREF = "/home"

/**
 * The Uploaded page has no create route: uploading happens in a dialog on the
 * page itself, which this query parameter pops open on arrival.
 */
export const UPLOAD_CONVERSATION_BASE_HREF = "/uploaded?upload=true"

/**
 * How the Home entry reads: Home is a Sofia surface, so the sidebar entry and
 * its `g h` command name it that, with the Coval mark in place of the house
 * glyph — under either sidebar. Search keywords keep the old vocabulary so
 * "home" and "dashboard" still find it.
 *
 * Applied by `buildAppShellNavigationItems` and `useAvailableCommands` rather
 * than baked into the config below, whose label still feeds command
 * `pathLabels` (and therefore the palette's "home" alias).
 */
export const SOFIA_HOME_NAV_OVERRIDE = {
  label: SOFIA_PRODUCT_NAME,
  icon: CovalMark,
  keywords: ["sofia", "home", "dashboard", "overview", "coval"],
} as const

export const siteNavigationConfig: {
  navItems: NavigationConfigItem[]
  navMenuItems: NavigationConfigItem[]
} = {
  navItems: [
    {
      label: "Home",
      base_href: HOME_BASE_HREF,
      icon: Home,
      keywords: ["home", "dashboard", "overview", "coval"],
    },
    {
      label: "Dashboards",
      base_href: "/dashboard",
      icon: LineChart,
      keywords: ["simulations", "conversations", "dashboard"],
    },
    {
      label: "Metrics",
      base_href: "/metrics",
      createBaseHref: "/metrics?create=true",
      icon: ENTITY_ICONS.metrics,
      keywords: ["metrics", "workflows", "tool call", "binary", "judge", "llm"],
    },
    {
      label: "Reports",
      base_href: "/reports",
      icon: FileText,
      keywords: ["reports", "saved", "multi-run"],
    },
    {
      label: "Alerts",
      base_href: "/alerts",
      createBaseHref: "/alerts/create",
      icon: Bell,
      keywords: ["alerts", "monitors", "thresholds", "notifications"],
    },
    {
      label: "Configure",
      base_href: "/agents",
      icon: BotMessageSquare,
      isSection: true,
      subItems: [
        {
          label: "Agents",
          base_href: "/agents",
          createBaseHref: "/agents?create=true",
          icon: ENTITY_ICONS.agents,
          keywords: ["agents"],
        },
        {
          label: "Personas",
          base_href: "/personas",
          createBaseHref: "/personas/new",
          icon: ENTITY_ICONS.personas,
          keywords: ["personas", "simulators", "voice ids"],
        },
        {
          label: "Test Sets",
          base_href: "/test-sets",
          createBaseHref: "/test-sets/create",
          icon: ENTITY_ICONS["test-sets"],
          keywords: ["test", "evaluate", "coval", "datasets"],
        },
        {
          label: "Templates",
          base_href: "/templates",
          createBaseHref: CREATE_TEMPLATE_INTENT_PATH,
          icon: Puzzle,
          keywords: ["templates", "test", "evaluate", "coval"],
        },
        {
          label: "Scheduled",
          base_href: "/scheduled",
          createBaseHref: "/simulate",
          icon: RefreshCw,
          keywords: ["scheduled", "runs"],
        },
        {
          label: "Simulated",
          base_href: "/simulated",
          createBaseHref: "/simulate",
          icon: BotMessageSquare,
          keywords: ["view", "runs", "simulations"],
        },
      ],
    },
    {
      label: "Conversations",
      base_href: "/uploaded",
      icon: Activity,
      isSection: true,
      keywords: ["conversations", "stream", "coval"],
      subItems: [
        {
          label: "Uploaded",
          base_href: "/uploaded",
          createBaseHref: UPLOAD_CONVERSATION_BASE_HREF,
          createLabel: "Upload conversation",
          icon: MessageSquareArrowUp,
          keywords: ["conversations", "live"],
        },
        {
          label: "Trace Search",
          base_href: "/traces",
          icon: Search,
          keywords: ["traces", "search", "otel", "opentelemetry", "spans"],
        },
      ],
    },
    {
      label: "Review",
      base_href: "/review/projects",
      icon: ClipboardCheck,
      isSection: true,
      keywords: ["review", "human", "feedback"],
      subItems: [
        {
          label: "Human Review",
          base_href: "/review/projects",
          icon: ClipboardCheck,
          keywords: ["review", "runs"],
        },
      ],
    },
    {
      label: "Benchmarks",
      base_href: "/benchmarks",
      icon: Gauge,
      isSection: true,
      featureFlag: "benchmarks",
      keywords: ["benchmark", "stt", "tts", "performance", "wer", "ttfc"],
      subItems: [
        {
          label: "STT",
          base_href: "/benchmarks/stt",
          icon: Mic,
          keywords: ["stt", "speech to text", "transcription", "wer"],
        },
        {
          label: "TTS",
          base_href: "/benchmarks/tts",
          icon: Volume2,
          keywords: ["tts", "text to speech", "synthesis", "ttfc"],
        },
      ],
    },
  ],
  navMenuItems: [
    {
      label: "Test Sets",
      base_href: "/test-sets",
      icon: ENTITY_ICONS["test-sets"],
    },
    {
      label: "Simulated",
      base_href: "/simulated",
      icon: BotMessageSquare,
    },
    {
      label: "Uploaded",
      base_href: "/uploaded",
      icon: Activity,
    },
  ],
}
